// src/services/implementations/fileencryption.service.impl.ts

import { hexToBytes } from "@/utils/hex.utils";
import { withErrorContext } from "@/utils/error.utils";
import { extractOriginalFileName, extractOriginalFileType, generateEncryptedFileName, generateEncryptedKeyFileName } from "@/utils/file.utils";
import type { LoggerService } from "@/services/abstract/LoggerService";
import type { CryptoService } from "@/services/abstract/poc/crypto.service.interface";
import { FileEncryptionService, type EncryptionResult, type DecryptionResult } from "@/services/abstract/poc/fileEncryption.service.interface";
import type { RecipientEncryptionResult, RecipientKey } from "@/types/poc/auth";
import type { Logger } from "@/types/Logger";

export class FileEncryptionServiceImpl extends FileEncryptionService {
  private readonly log: Logger;

  constructor(
    private readonly cryptoService: CryptoService,
    loggerService: LoggerService
  ) {
    super();
    this.log = loggerService.createLogger({ namespace: 'six3ro:crypto:file' });
  }

  async encryptFile(
    file: File,
    ukek: Uint8Array,
    recipients: Array<{ id: string, publicKey: Uint8Array }>
  ): Promise<EncryptionResult> {
    return await withErrorContext('File encryption failed', async() => {
      this.log.info('Starting encryption for file:', file.name);

      // Step 1: Generate random 256-bit file key
      const fileKey = this.generateFileKey();
      this.log.debug('Generated 256-bit file key');
      this.log.trace('File key details', { length: fileKey.length, type: fileKey.constructor.name });

      // Validate file key is exactly 32 bytes for AES-256
      const EXPECTED_KEY_SIZE = 32;
      if (fileKey.length !== EXPECTED_KEY_SIZE) {
        throw new Error(`Invalid file key length: ${fileKey.length} bytes. Expected ${EXPECTED_KEY_SIZE} bytes for AES-256`);
      }

      const HEX_BASE = 16;
      const PAD_LENGTH = 2;
      const PREVIEW_BYTES = 16;
      this.log.trace('File key hex:', Array.from(fileKey).map(b => b.toString(HEX_BASE).padStart(PAD_LENGTH, '0')).join(''));

      // Step 2: Read file data
      const fileData = await FileEncryptionService.readFileAsUint8Array(file);
      this.log.debug('Read file data:', fileData.length, 'bytes');
      this.log.trace('File data details', {
        type: fileData.constructor.name,
        preview: Array.from(fileData.slice(0, PREVIEW_BYTES)).map(b => b.toString(HEX_BASE).padStart(PAD_LENGTH, '0')).join('')
      });

      // Step 3: Encrypt file with AES-GCM-256
      this.log.debug('About to encrypt file with AES-GCM...');
      const encryptedFileResult = await this.cryptoService.encrypt(fileData, fileKey);
      const encryptedFileBlob = FileEncryptionService.combineEncryptedComponents(
        encryptedFileResult.encryptedData,
        encryptedFileResult.iv,
        encryptedFileResult.authTag
      );
      this.log.debug('File encrypted with AES-GCM-256');

      // Step 4: Encrypt file key with UKEK (User Key Encrypted Key)
      this.log.debug('About to encrypt file key with UKEK...');
      this.log.trace('UKEK details', { length: ukek.length, type: ukek.constructor.name });

      // Validate UKEK is exactly 32 bytes for AES-256
      if (ukek.length !== EXPECTED_KEY_SIZE) {
        throw new Error(`Invalid UKEK length: ${ukek.length} bytes. Expected ${EXPECTED_KEY_SIZE} bytes for AES-256`);
      }

      this.log.trace('UKEK hex:', Array.from(ukek).map(b => b.toString(HEX_BASE).padStart(PAD_LENGTH, '0')).join(''));
      const encryptedFileKeyResult = await this.cryptoService.encrypt(fileKey, ukek);
      const encryptedFileKeyBlob = FileEncryptionService.combineEncryptedComponents(
        encryptedFileKeyResult.encryptedData,
        encryptedFileKeyResult.iv,
        encryptedFileKeyResult.authTag
      );
      this.log.debug('File key encrypted with UKEK');

      // Step 5: Create File objects with proper names
      const encryptedFile = new File([encryptedFileBlob], generateEncryptedFileName(file.name), {
        type: 'application/octet-stream'
      });

      const encryptedFileKey = new File([encryptedFileKeyBlob], generateEncryptedKeyFileName(file.name), {
        type: 'application/octet-stream'
      });

      // Step 6: Encrypt file key for each recipient using ML-KEM
      const recipientResults: RecipientEncryptionResult[] = await Promise.all(
        recipients.map(async(recipient) => {
          this.log.debug('Encrypting file key for recipient:', recipient.id);

          // Encapsulate to create shared secret between Alice and Bob
          const { cipherText, sharedSecret } = this.cryptoService.encapsulateMlKem(recipient.publicKey);

          // Encrypt the file key with the shared secret
          const encryptedKeyForRecipient = await this.cryptoService.encrypt(fileKey, sharedSecret);

          this.log.debug('File key encrypted for recipient:', recipient.id);

          return {
            recipientId: recipient.id,
            cipherText, // Bob can use this with his private key to recover the shared secret
            encryptedFileKey: encryptedKeyForRecipient // File key encrypted with shared secret
          };
        })
      );

      this.log.info('Encryption complete:', recipientResults.length, 'recipients');

      return {
        encryptedFile,
        encryptedFileKey,
        recipientResults,
        originalFileName: file.name,
        originalFileSize: file.size,
        originalFileType: file.type === '' ? 'application/octet-stream' : file.type
      };
    });
  }

  // =================== CORE FILE WORKFLOWS ===================
  async decryptFileWithEncryptedKey(
    encryptedFile: File,
    encryptedFileKey: File,
    ukek: Uint8Array
  ): Promise<DecryptionResult> {
    return await withErrorContext('File decryption with key failed', async() => {
      this.log.info('Starting decryption for file:', encryptedFile.name);

      const fileKey = await this.decryptFileKey(encryptedFileKey, ukek);
      const decryptedFile = await this.decryptFileData(encryptedFile, fileKey);

      const result = {
        decryptedFile,
        originalFileName: extractOriginalFileName(encryptedFile.name),
        originalFileSize: decryptedFile.size,
        originalFileType: extractOriginalFileType(encryptedFile.type)
      };

      this.log.info('File decryption completed:', result.originalFileName);
      return result;
    });
  }

  // =================== RECIPIENT KEY MANAGEMENT ===================

  async encryptFileKeyForRecipients(
    fileKey: Uint8Array,
    targetRecipients: RecipientKey[]
  ): Promise<RecipientEncryptionResult[]> {

      if (targetRecipients.length === 0) {
        this.log.warn('No recipients found for file key encryption');
        return [];
      }

      this.log.debug('Encrypting file key for', targetRecipients.length, 'recipients');

      const results = await Promise.all(
        targetRecipients.map(async(recipient) =>
          await this.encryptFileKeyForSingleRecipient(fileKey, recipient)
        )
      );

      this.log.debug('File key encrypted for', results.length, 'recipients');
      return results;

  }

    async decryptFileKeyForRecipient(
    recipientResult: RecipientEncryptionResult,
    recipientPrivateKey: Uint8Array
    ): Promise<Uint8Array> {
        return await withErrorContext('Failed to decrypt file key for recipient', async() => {
            this.log.debug('Decrypting file key for recipient:', recipientResult.recipientId);

            // Step 1: Decapsulate to get shared secret using your crypto service
            const decapsulationResult = this.cryptoService.decapsulateMlKem(
            recipientResult.cipherText,
            recipientPrivateKey
            );

            // Step 2: Decrypt the file key using the shared secret and your crypto service
            // recipientResult.encryptedFileKey already has the structure we need
            const decryptedFileKey = await this.cryptoService.decrypt(
            recipientResult.encryptedFileKey.encryptedData,
            recipientResult.encryptedFileKey.iv,
            recipientResult.encryptedFileKey.authTag,
            decapsulationResult.sharedSecret
            );

            this.log.debug('File key decrypted for recipient:', recipientResult.recipientId);
            return decryptedFileKey;
        });
    }

// =================== FILE KEY FACILITATION ===================

  async reEncryptFileKeyForRecipient(
    facilitatorEncryption: RecipientEncryptionResult,
    facilitatorPrivateKey: Uint8Array,
    recipientPublicKey: Uint8Array
  ): Promise<{ cipherText: Uint8Array; encryptedFileKey: { encryptedData: Uint8Array; iv: Uint8Array; authTag: Uint8Array } }> {
    this.log.debug('Re-encrypting file key for new recipient');

    const rawFileKey = await this.decryptFileKeyForRecipient(
      facilitatorEncryption,
      facilitatorPrivateKey
    );

    const { cipherText, sharedSecret } = this.cryptoService.encapsulateMlKem(recipientPublicKey);
    const encryptedFileKey = await this.cryptoService.encrypt(rawFileKey, sharedSecret);

    this.log.debug('File key re-encrypted for new recipient');
    return { cipherText, encryptedFileKey };
  }

// =================== ADDITIONAL CONVENIENCE METHODS ===================

  async encryptFileForSpecificRecipients(
    file: File,
    ukek: Uint8Array,
    targetRecipients: RecipientKey[]
  ): Promise<EncryptionResult> {

      const fileKey = this.generateFileKey();
      const fileData = await FileEncryptionService.readFileAsUint8Array(file);

      const encryptionTasks = await Promise.all([
        this.createEncryptedFile(file, fileData, fileKey),
        this.createEncryptedFileKey(file, fileKey, ukek),
        this.encryptFileKeyForRecipients(fileKey, targetRecipients)
      ]);

      const [encryptedFile, encryptedFileKey, recipientResults] = encryptionTasks;

      return {
        encryptedFile,
        encryptedFileKey,
        recipientResults,
        originalFileName: file.name,
        originalFileSize: file.size,
        originalFileType: file.type
      };
  }

  async decryptFileAsRecipient(
    encryptedFile: File,
    recipientResult: RecipientEncryptionResult,
    recipientPrivateKey: Uint8Array
  ): Promise<DecryptionResult> {
    return await withErrorContext('File decryption as recipient failed', async() => {
      this.log.debug('Decrypting file as recipient:', recipientResult.recipientId);

      const fileKey = await this.decryptFileKeyForRecipient(
        recipientResult,
        recipientPrivateKey
      );

      const decryptedFile = await this.decryptFileData(encryptedFile, fileKey);

      const result = {
        decryptedFile,
        originalFileName: extractOriginalFileName(encryptedFile.name),
        originalFileSize: decryptedFile.size,
        originalFileType: extractOriginalFileType(encryptedFile.type)
      };

      this.log.info('File decryption as recipient completed:', result.originalFileName);
      return result;
    });
  }

  async addRecipientsToEncryptedFile(
    fileKey: Uint8Array,
    newRecipients: RecipientKey[]
  ): Promise<RecipientEncryptionResult[]> {
    return await withErrorContext('Failed to add recipients to encrypted file', async() => await this.encryptFileKeyForRecipients(fileKey, newRecipients));
  }

  // =================== PRIVATE HELPER METHODS ===================

  private async createEncryptedFile(
    originalFile: File,
    fileData: Uint8Array,
    fileKey: Uint8Array
  ): Promise<File> {
    // Use your crypto service's encrypt method
    const encryptedResult = await this.cryptoService.encrypt(fileData, fileKey);
    const encryptedBlob = FileEncryptionService.combineEncryptedComponents(
      encryptedResult.encryptedData,
      encryptedResult.iv,
      encryptedResult.authTag
    );

    return new File([encryptedBlob], generateEncryptedFileName(originalFile.name), {
      type: 'application/octet-stream'
    });
  }

  private async createEncryptedFileKey(
    originalFile: File,
    fileKey: Uint8Array,
    ukek: Uint8Array
  ): Promise<File> {
    // Use your crypto service's encrypt method
    const encryptedKeyResult = await this.cryptoService.encrypt(fileKey, ukek);
    const encryptedKeyBlob = FileEncryptionService.combineEncryptedComponents(
      encryptedKeyResult.encryptedData,
      encryptedKeyResult.iv,
      encryptedKeyResult.authTag
    );

    return new File([encryptedKeyBlob], generateEncryptedKeyFileName(originalFile.name), {
      type: 'application/octet-stream'
    });
  }

    private async encryptFileKeyForSingleRecipient(
    fileKey: Uint8Array,
    recipient: RecipientKey
    ): Promise<RecipientEncryptionResult> {
    this.log.debug('Encrypting file key for recipient:', recipient.id);

    // Step 1: Encapsulate using your crypto service
    const encapsulationResult = this.cryptoService.encapsulateMlKem(recipient.publicKey);

    // Step 2: Encrypt the file key with the shared secret using your crypto service
    const encryptedKeyResult = await this.cryptoService.encrypt(
        fileKey,
        encapsulationResult.sharedSecret
    );

    this.log.debug('File key encrypted for recipient:', recipient.id);

    return {
        recipientId: recipient.id,
        cipherText: encapsulationResult.cipherText,
        encryptedFileKey: encryptedKeyResult // Use the structured result directly
    };
  }

  private async decryptFileKey(
    encryptedFileKey: File,
    ukek: Uint8Array
  ): Promise<Uint8Array> {
    const encryptedKeyData = await FileEncryptionService.readFileAsUint8Array(encryptedFileKey);
    const { data, iv, authTag } = FileEncryptionService.extractEncryptedComponents(encryptedKeyData);

    // Use your crypto service's decrypt method
    return await this.cryptoService.decrypt(data, iv, authTag, ukek);
  }

private async decryptFileData(
  encryptedFile: File,
  fileKey: Uint8Array
): Promise<File> {
  const encryptedData = await FileEncryptionService.readFileAsUint8Array(encryptedFile);
  const { data, iv, authTag } = FileEncryptionService.extractEncryptedComponents(encryptedData);

  // Use your crypto service's decrypt method
  const decryptedData = await this.cryptoService.decrypt(data, iv, authTag, fileKey);

  // Convert to proper Uint8Array with ArrayBuffer to satisfy File constructor
  const properDecryptedData = new Uint8Array(decryptedData);

  const originalFileName = extractOriginalFileName(encryptedFile.name);
  const originalFileType = extractOriginalFileType(encryptedFile.name);

  return new File([properDecryptedData], originalFileName, {
    type: originalFileType
  });
}
  // =================== OVERRIDE PROTECTED METHODS ===================

  /**
   * Generate file encryption key using your crypto service
   * Your generateAesKey() returns hex string, so convert to Uint8Array
   */
  protected generateFileKey(): Uint8Array {
    const keyHex = this.cryptoService.generateAesKey();
    return hexToBytes(keyHex);
  }
}
